import {
  DocumentNode,
  gql,
  OperationVariables,
  SubscriptionHookOptions,
  SubscriptionResult,
  TypedDocumentNode,
  useApolloClient,
  useQuery,
  useSubscription,
} from '@apollo/client';
import { print } from 'graphql';
import isNil from 'lodash/isNil';
import { useEffect, useMemo, useState } from 'react';
import { useIdle, UseIdleProps } from './useIdle';

type ExtendedSubscriptionResult<TData, TVariables> = SubscriptionResult<TData, TVariables> & {
  restart: () => void;
};

type SubscriptionOptions<
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
> = SubscriptionHookOptions<TData, TVariables> & {
  useIdleOptions?: UseIdleProps;
  /**
   * If the condition is true, the subscription will be skipped
   */
  skipCondition?: (data: NoInfer<TData>) => boolean;
};

/**
 * This hook is a combination of `useSubscription` and `useQuery` hooks.
 * It first tries to use the subscription hook, and if it fails, it falls back to the query hook.
 *
 * ### How to disable Websocket subscription and fallback to polling:
 *
 * Set Localhost key `disableWebsocket` to `true` in the LocalStorage.
 *
 * ```ts
 * localStorage.setItem('disableWebsocket', 'true');
 * ```
 *
 * @example
 *
 * ```tsx
 * import useSubscriptionFallback from '@/hooks/useSubscriptionFallback';
 *
 * const MyComponent = () => {
 *  const { data, loading, error } = useSubscriptionFallback(SUBSCRIPTION, {
 *    skipCondition: (data) => data?.async_job.length === 0,
 *  });
 *
 *  ...
 * }
 * ```
 */
const useSubscriptionFallback = <TData = any, TVariables extends OperationVariables = OperationVariables>(
  subscription: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options: SubscriptionOptions<TData, TVariables>,
): ExtendedSubscriptionResult<TData, TVariables> => {
  const client = useApolloClient();

  const [latestData, setLatestData] = useState<TData | undefined>(undefined);

  const { isIdle } = useIdle({
    ...options?.useIdleOptions,
  });

  const skip = useMemo(() => {
    const shouldSkipOnData =
      !isNil(latestData) && !isNil(options.skipCondition) ? options.skipCondition(latestData) : false;
    const result = isIdle || options.skip || shouldSkipOnData;
    return result;
  }, [latestData, options, isIdle]);

  const query = useMemo(
    () => gql`
      ${print(subscription).replace(/^subscription/i, 'query')}
    `,
    [subscription],
  );

  const subscriptionResult = useSubscription(subscription, {
    ...options,
    skip,
    onError: (error) => {
      console.warn('Websocket subscription failed, falling back to polling');
      console.error(error);
      options?.onError?.(error);
    },
    onData: (...args) => {
      const data = args[0].data?.data;
      if (!isNil(data)) {
        setLatestData(data);
      }

      options?.onData?.(...args);
    },
  });

  const isSubscriptionWorking = !subscriptionResult.error || subscriptionResult.loading;

  const queryResult = useQuery(query, {
    pollInterval: 5 * 1000, // 5 seconds
    fetchPolicy: 'cache-and-network',
    variables: {
      ...options.variables,
    },
    skip: isSubscriptionWorking || skip,
    onCompleted: (data) => {
      if (!isNil(options?.onData)) {
        options.onData({
          data: {
            data,
            loading: false,
            error: undefined,
            variables: options.variables,
          },
          client,
        });
      }

      setLatestData(data);
    },
  });

  const serializedSubscriptionVariables = JSON.stringify(options.variables);

  useEffect(() => {
    setLatestData(undefined);
  }, [serializedSubscriptionVariables, options.skip]);

  if (isSubscriptionWorking) {
    return {
      ...subscriptionResult,
      data: latestData ?? subscriptionResult.data,
      restart: () => {
        setLatestData(undefined);
      },
    };
  }

  // If the subscription is not working, return the query result with restart method
  return {
    data: latestData ?? queryResult.data,
    loading: queryResult.loading,
    error: queryResult.error,
    variables: queryResult.variables as TVariables | undefined,
    restart: () => {
      setLatestData(undefined);
      queryResult.refetch();
    },
  };
};

export default useSubscriptionFallback;
